import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { staffService } from '../../services';
import { useUser } from '../../context/UserContext';
import { useDialog } from '../../context/DialogContext';
import { AdminPageHeader, DataTable, DropChip, DateChip, SearchField, StatTile, Btn } from '../../components/admin/shell';
import { activityColumns } from '../../components/admin/activityColumns';
import { CAPABILITIES } from '../../utils/staffCapabilities';

const KINDS = ['session', 'view', 'search', 'write', 'export', 'denied'];
const SEVERITIES = ['info', 'notice', 'warning', 'critical'];

function DiffPanel({ row }) {
  let before = null; let after = null;
  try { before = row.beforeJson ? JSON.parse(row.beforeJson) : null; } catch { before = row.beforeJson; }
  try { after = row.afterJson ? JSON.parse(row.afterJson) : null; } catch { after = row.afterJson; }
  const keys = Array.from(new Set([...(before && typeof before === 'object' ? Object.keys(before) : []), ...(after && typeof after === 'object' ? Object.keys(after) : [])]));
  const show = (v) => (v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v));
  const changed = keys.filter((k) => show(before?.[k]) !== show(after?.[k]));
  if (changed.length === 0) return <div className="font-mono text-[11px] text-[#6B7280]">NOTHING TO COMPARE — {row.action?.toUpperCase() || 'WRITE'}</div>;
  return (
    <div className="grid gap-x-[14px] gap-y-[6px]" style={{ gridTemplateColumns: 'minmax(80px, 180px) minmax(0,1fr) minmax(0,1fr)' }}>
      {['FIELD', 'BEFORE', 'AFTER'].map((h) => <div key={h} className="font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-[#6B7280]">{h}</div>)}
      {changed.map((k) => (
        <React.Fragment key={k}>
          <div className="font-mono text-[11px] font-bold tracking-[0.05em]">{k}</div>
          <div className="break-all font-mono text-[12px] text-[#6B7280]">{show(before?.[k])}</div>
          <div className="break-all font-mono text-[12px] text-[#1A1A1A]">{show(after?.[k])}</div>
        </React.Fragment>
      ))}
    </div>
  );
}

/** Lives in components/admin/activityColumns.jsx now (the drawer needs it without the barrel). */
export { activityColumns };

/** SYSTEM › AUDIT LOG — spec/audit.md. Every write, view, search, export and refusal. */
export default function AuditPage() {
  const { can, isSuperadmin } = useUser();
  const { showAlert } = useDialog();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [people, setPeople] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(new Set());
  const [q, setQ] = useState(params.get('q') || '');

  const filters = useMemo(() => ({
    actor: params.get('actor') || '', kind: params.get('kind') || '', severity: params.get('severity') || '',
    capability: params.get('capability') || '', target: params.get('target') || '', q: params.get('q') || '',
    from: params.get('from') || '', to: params.get('to') || '', page: Number(params.get('page') || 1),
  }), [params]);
  const set = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries({ ...patch, page: patch.page ?? 1 }).forEach(([k, v]) => { if (v) next.set(k, String(v)); else next.delete(k); });
    setParams(next, { replace: true });
  };

  useEffect(() => { (async () => { const r = await staffService.activitySummary(30); if (r.success) { setSummary(r.data); setPeople(r.data.people || []); } })(); }, []);
  const load = useCallback(async () => {
    setLoading(true);
    const r = await staffService.activity({ ...filters, pageSize: 50, to: filters.to ? `${filters.to}T23:59:59Z` : '' });
    setLoading(false);
    if (r.success) setData(r.data);
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const r = await staffService.exportActivity({ ...filters, page: undefined });
    if (!r.success) { await showAlert({ title: 'EXPORT FAILED', message: r.error, type: 'error', theme: 'admin' }); return; }
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a'); a.href = url; a.download = `party-resume-activity-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  const toggle = (id) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <>
      <AdminPageHeader
        section="audit"
        meta="Every write, view, search, export and refusal in the panel."
        actions={can('activity', 'export') ? <Btn variant="quiet" onClick={exportCsv}>EXPORT CSV</Btn> : null}
      />
      <div className="flex flex-col gap-4">
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
          {[['SESSIONS', summary?.sessions], ['WRITES', summary?.writes], ['WARNINGS', summary?.warnings], ['CRITICALS', summary?.criticals], ['DENIED', summary?.denied]].map(([l, v]) => (
            <StatTile key={l} label={`${l} · 30D`} value={v == null ? '—' : v.toLocaleString()} small />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DropChip label="PERSON" value={filters.actor} onChange={(v) => set({ actor: v })} options={[{ value: '', label: 'ALL' }, ...people.map((p) => ({ value: p.userId, label: p.name.toUpperCase() }))]} />
          <DropChip label="SECTION" value={filters.capability} onChange={(v) => set({ capability: v })} options={[{ value: '', label: 'ALL' }, ...CAPABILITIES.map((c) => ({ value: c.key, label: c.label }))]} />
          <DropChip label="KIND" value={filters.kind} onChange={(v) => set({ kind: v })} options={[{ value: '', label: 'ALL' }, ...KINDS.map((k) => ({ value: k, label: `${k.toUpperCase()}${data?.kindCounts?.[k] != null ? ` (${data.kindCounts[k]})` : ''}` }))]} />
          <DropChip label="SEVERITY" value={filters.severity} onChange={(v) => set({ severity: v })} options={[{ value: '', label: 'ALL' }, ...SEVERITIES.map((k) => ({ value: k, label: k.toUpperCase() }))]} />
          <DateChip label="FROM" value={filters.from} onChange={(v) => set({ from: v })} />
          <DateChip label="TO" value={filters.to} onChange={(v) => set({ to: v })} />
          <SearchField value={q} onChange={setQ} onSubmit={() => set({ q })} placeholder="SEARCH" />
          {filters.target && <button type="button" onClick={() => set({ target: '' })} className="font-mono text-[11px] font-bold uppercase tracking-[0.05em] underline">RECORD: {filters.target} ✕</button>}
        </div>
        <DataTable
          columns={activityColumns({ onToggle: toggle, open })}
          rows={data?.rows || []}
          loading={loading}
          rowKey={(r) => r.id}
          onRowClick={(r) => (r.kind === 'write' ? toggle(r.id) : null)}
          expanded={(r) => (open.has(r.id) ? <DiffPanel row={r} /> : null)}
          hasActions={isSuperadmin}
          actions={(r) => (r.kind === 'denied' && isSuperadmin && r.actorUserId ? [{ label: 'OPEN IN STAFF →', onClick: () => navigate(`/admin/staff/${r.actorUserId}`) }] : [])}
          total={data?.total ?? 0}
          page={filters.page}
          pageSize={50}
          onPage={(p) => set({ ...filters, page: p })}
          footerLeft={`${(data?.total ?? 0).toLocaleString()} rows · views and searches kept two years, writes forever`}
        />
      </div>
    </>
  );
}
